// src/components/Landing/PublicPricingPage.jsx
// Marketing pricing page — accessible without login. Pulls the same v2
// numbers as server/config/plans.js + client/src/pages/PricingPage.js so a
// prospect sees what a logged-in account sees, just framed for evaluation
// rather than billing actions. Matches PlaceholderLanding's Chakra style.

import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { initializeAnalytics, trackPageView } from '../../utils/analytics';
import {
	Box,
	Flex,
	Text,
	Heading,
	Button,
	HStack,
	VStack,
	Container,
} from '@chakra-ui/react';

const BRAND = '#3D1E6D';
const BRAND_DEEP = '#241043';
const ACCENT = '#FB8601';
const ACCENT_DEEP = '#B14E00';
const INK = '#111113';
const MUTED = '#6B7280';
const CREAM = '#FBFBF7';
const LINE = 'rgba(17, 17, 19, 0.08)';
const HEAD_FONT = "'Poppins', sans-serif";

// Software-only Essential, banded by guest count. Price scales with size
// because more guests = more concurrent connections + more support tickets.
const ESSENTIAL_SOFTWARE_BANDS = [
	{
		size: 'Up to 200 guests',
		price: '₦15,000',
		bestFor: 'Small weddings, corporate dinners',
	},
	{
		size: '201–400 guests',
		price: '₦25,000',
		bestFor: 'Typical Lagos wedding · your own tablets',
		highlight: true,
	},
	{
		size: '401+ guests',
		price: '₦35,000',
		bestFor: 'Big weddings · your own tablets',
	},
];

const PER_EVENT_BUNDLES = [
	{
		label: 'Essential',
		range: 'Any size · software only',
		bestFor: 'Pay-as-you-go software when you already have tablets and team',
		price: '₦15K–₦35K',
		priceSub: 'per event · by guest count',
		inclusions: [
			'Full Eventory software for one event',
			'Branded QR menu for guests',
			'Live kitchen + server dashboards',
			'Dietary tags, allergies, post-event CSV',
			'You provide tablets and on-floor team',
		],
		softwareOnly: true,
		bands: ESSENTIAL_SOFTWARE_BANDS,
	},
	{
		label: 'Standard',
		range: '201–400 guests · full service',
		bestFor: 'The typical Lagos society wedding — we bring tablets + Ops',
		price: '₦95,000',
		priceSub: 'per event · all-in',
		highlight: true,
		badge: 'Most popular',
		inclusions: [
			'Everything in Essential, plus:',
			'7 pre-loaded tablets brought to your venue',
			'Ops Manager + 1 runner on-site (10-hour coverage)',
			'Day-before setup of menu, tables, stations',
			'Multi-station kitchen routing + real-time monitoring',
		],
	},
	{
		label: 'Premium',
		range: '401+ guests · full service',
		bestFor: 'Big society weddings, brand activations, gov receptions',
		price: '₦135,000',
		priceSub: 'per event · all-in',
		badge: 'Best for big weddings',
		inclusions: [
			'Everything in Standard, plus:',
			'10 pre-loaded tablets',
			'Ops Manager + 2 runners + setup crew',
			'Dedicated event-day support number',
			'Same-day post-event review with the planner',
		],
	},
];

// Service add-ons — sold à la carte to Free / Essential / Oga customers
// who need tablets and Ops on a specific event without going all the way
// to the Standard or Premium bundle. Pack prices are tuned so the
// Standard / Premium bundle is always the cheaper full-service path for
// ≥200-guest events; the Essential pack covers the ≤200-guest case where
// there's no full-service bundle SKU.
const SERVICE_ADD_ONS = [
	{ label: 'Essential pack', range: 'Up to 200 guests', kit: '5 tablets · Ops Manager', price: '₦55,000' },
	{ label: 'Standard pack', range: '201–400 guests', kit: '7 tablets · Ops Mgr + 1 runner', price: '₦80,000' },
	{ label: 'Premium pack', range: '401+ guests', kit: '10 tablets · Ops Mgr + 2 runners', price: '₦120,000' },
];

// Public policy — kept identical to server/config/plans.js
// SERVICE_ADD_ON_POLICY so what we promise matches what we deliver.
const ADD_ON_POLICY = [
	{
		head: 'Notice required',
		body: 'Ops Manager bookings need 14 days lead time. Tablets need 7 days. Late requests are honoured at 1.5× the pack price, subject to availability.',
	},
	{
		head: 'On-site coverage',
		body: 'Standard 10 hours: Ops Manager arrives 2 hours before service, leaves 1 hour after the final plate. Overtime ₦7,500/hour past 10 hours. Multi-day events are quoted separately.',
	},
	{
		head: 'What they do',
		body: 'Day-before menu / table / station setup in Eventory. Day-of tablet distribution and live order-flow monitoring. First-line tech support if anything goes wrong with the software. End-of-night report walkthrough. Pack-up.',
	},
	{
		head: 'What they don\'t do',
		body: 'No cooking or plating. No serving plates directly (unless a runner is included). No table setup, linens, or décor. They don\'t hire or manage your kitchen or floor staff.',
	},
	{
		head: 'Cancellation',
		body: '≥48 hours before service: full refund. <48 hours: you forfeit the logistics portion (typically ~₦15,000). Day-of: the full pack price is forfeited.',
	},
	{
		head: 'Hardware care',
		body: 'Tablets ship with their own SIM data so they work even when the venue WiFi is down. Lost or damaged tablet: ₦25,000 replacement fee per unit. You return everything to the Ops Manager at end of service.',
	},
];

// What you GET on the sub
const SUBSCRIPTION_INCLUDES = [
	'Unlimited events per month',
	'All premium software features (offline mode, multi-admin, branding, CSV exports)',
	'Priority email + WhatsApp support',
	'Cancel any time — no lock-in',
];

// What you DON'T get on the sub (vs the bundle) — explicit so the value
// of the bundle stays visible.
const SUBSCRIPTION_EXCLUDES = [
	'No on-site Ops Manager — you run setup and floor management yourself',
	'No pre-loaded tablets — you provide your own hardware',
	'No on-the-day runners — bring your own team',
];

const FAQ_TEASERS = [
	{
		q: 'When do I owe Eventory anything?',
		a: 'Your first 2 events are free — software only, you bring tablets and team. After that: pay ₦15K per event (software only), book a full-service bundle for bigger weddings (₦95K Standard or ₦135K Premium — tablets + Ops Manager included), or subscribe to Oga for ₦75K/month and run unlimited events.',
	},
	{
		q: 'Why is Essential software-only? Where are the tablets?',
		a: 'Tablets and Ops Managers cost real cash, so they\'re never bundled into the cheapest plan. Need them on a one-off event? Attach a service pack (₦55K / ₦80K / ₦120K by guest count). Most small-event caterers already own tablets — that\'s why Essential is ₦15K software-only.',
	},
	{
		q: 'What\'s the difference between Essential and the Oga subscription?',
		a: 'Both are software-only. Essential is ₦15K/event — pay as you go. Oga is ₦75K/month for unlimited events. At 5 events/month they cost the same; below 5 stay on Essential, above 5 switch to Oga.',
	},
	{
		q: 'What does the Ops Manager actually do? And what don\'t they do?',
		a: 'They set up your menu, tables, and kitchen stations in Eventory the day before; distribute tablets on-site; monitor order flow live and flag issues; provide first-line tech support; walk you through the post-event report; and pack up. They do not cook, serve plates (unless a runner is on the pack), set up the room, or manage your own staff. Standard coverage is 10 hours.',
	},
	{
		q: 'How much notice do you need for tablets and Ops?',
		a: 'Ops Manager: 14 days in advance. Tablets: 7 days. Late requests honoured at 1.5× the pack price if we have capacity. Cancellations ≥48h before service are fully refunded; under 48h forfeits ~₦15K logistics cost; day-of forfeits the full pack price.',
	},
	{
		q: 'What about guest food/drink upgrades?',
		a: 'If your event has paid premium items (e.g. a cash bar), guests pay via Paystack at the table. Eventory takes 2% of what they pay, capped at ₦5,000 per transaction. The remaining 98% lands in your bank automatically.',
	},
];

const Kicker = ({ children, color = ACCENT }) => (
	<Text
		as="span"
		fontSize={{ base: '12px', md: '13px' }}
		fontWeight="700"
		letterSpacing="0.12em"
		textTransform="uppercase"
		color={color}
		fontFamily={HEAD_FONT}
	>
		{children}
	</Text>
);

const PriceCard = ({ tier }) => (
	<Box
		bg="white"
		border={`1.5px solid ${tier.highlight ? ACCENT : LINE}`}
		borderRadius="20px"
		p={{ base: 7, md: 8 }}
		position="relative"
		boxShadow={tier.highlight ? `0 18px 40px ${ACCENT}1f` : '0 8px 24px rgba(17,17,19,0.04)'}
		display="flex"
		flexDirection="column"
	>
		{tier.badge && (
			<Box
				position="absolute"
				top="-14px"
				left="50%"
				transform="translateX(-50%)"
				bg={tier.highlight ? ACCENT : BRAND}
				color="white"
				px={4}
				py={1.5}
				fontSize="11px"
				fontWeight="700"
				letterSpacing="0.08em"
				textTransform="uppercase"
				borderRadius="full"
				whiteSpace="nowrap"
				boxShadow={`0 6px 18px ${tier.highlight ? ACCENT : BRAND}33`}
			>
				{tier.badge}
			</Box>
		)}
		<Text fontFamily={HEAD_FONT} fontWeight="700" color={BRAND} fontSize="lg">{tier.label}</Text>
		<Text fontSize="sm" color={MUTED} mb={4}>{tier.range}</Text>
		<Heading fontFamily={HEAD_FONT} fontWeight="800" fontSize="44px" lineHeight="1" color={INK}>
			{tier.price}
		</Heading>
		<Text fontSize="sm" color={MUTED} mt={1} mb={4}>{tier.priceSub}</Text>
		{tier.bestFor && (
			<Box
				bg={tier.highlight ? 'rgba(251, 134, 1, 0.08)' : 'rgba(61, 30, 109, 0.05)'}
				border={`1px solid ${tier.highlight ? 'rgba(251, 134, 1, 0.25)' : 'rgba(61, 30, 109, 0.12)'}`}
				borderRadius="10px"
				px={3}
				py={2.5}
				mb={5}
			>
				<Text fontSize="xs" color={tier.highlight ? ACCENT_DEEP : BRAND} fontWeight="600" lineHeight="1.4">
					{tier.bestFor}
				</Text>
			</Box>
		)}
		<VStack align="stretch" spacing={3} mb={tier.bands ? 5 : 8} flex={1}>
			{tier.inclusions.map((item) => (
				<HStack key={item} align="flex-start" spacing={3}>
					<Box mt="6px" w="6px" h="6px" borderRadius="full" bg={ACCENT} flexShrink={0} />
					<Text fontSize="sm" color={INK} lineHeight="1.5">{item}</Text>
				</HStack>
			))}
		</VStack>
		{tier.bands && (
			<Box mb={6} p={3} bg="rgba(61, 30, 109, 0.04)" borderRadius="12px">
				<Text fontSize="11px" fontWeight="700" color={BRAND} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
					Price by guest count
				</Text>
				<VStack align="stretch" spacing={1.5}>
					{tier.bands.map((b) => (
						<Flex key={b.size} justify="space-between" align="center">
							<Text fontSize="13px" color={INK} fontWeight={b.highlight ? '700' : '500'}>
								{b.size}
							</Text>
							<Text fontSize="13px" fontWeight="700" color={INK}>
								{b.price}
							</Text>
						</Flex>
					))}
				</VStack>
			</Box>
		)}
		<Button
			as={RouterLink}
			to={`/register?intent=${tier.label.toLowerCase()}`}
			bg={tier.highlight ? ACCENT : 'white'}
			color={tier.highlight ? 'white' : BRAND}
			border={`1.5px solid ${tier.highlight ? ACCENT : BRAND}`}
			borderRadius="full"
			h="48px"
			fontWeight="700"
			textDecoration="none"
			_hover={{
				bg: tier.highlight ? ACCENT_DEEP : BRAND,
				color: 'white',
				borderColor: tier.highlight ? ACCENT_DEEP : BRAND,
				textDecoration: 'none',
			}}
		>
			Get started
		</Button>
	</Box>
);

const PublicPricingPage = () => {
	useEffect(() => {
		initializeAnalytics();
		trackPageView('pricing');
	}, []);

	return (
		<Box w="100%" minH="100vh" bg="white">
			{/* Slim header (logo + back to home) */}
			<Box bg={CREAM} borderBottom={`1px solid ${LINE}`} py={5}>
				<Container maxW="1200px" px={{ base: 6, md: 8 }}>
					<Flex justify="space-between" align="center">
						<RouterLink to="/" style={{ textDecoration: 'none' }}>
							<Box as="img" src="/images/eventory-logo.svg" alt="Eventory" h={{ base: '24px', md: '30px' }} />
						</RouterLink>
						<HStack spacing={{ base: 3, md: 6 }}>
							<Box as={RouterLink} to="/" fontSize="14px" color={INK} _hover={{ color: ACCENT_DEEP }}>
								Home
							</Box>
							<Box as={RouterLink} to="/demo" fontSize="14px" color={INK} _hover={{ color: ACCENT_DEEP }}>
								Demo
							</Box>
							<Button
								as={RouterLink}
								to="/register"
								bg={ACCENT}
								color="white"
								borderRadius="full"
								px={5}
								h="40px"
								fontSize="14px"
								fontWeight="700"
								_hover={{ bg: ACCENT_DEEP }}
								textDecoration="none"
							>
								Get started
							</Button>
						</HStack>
					</Flex>
				</Container>
			</Box>

			{/* Hero */}
			<Box bg="white" py={{ base: 16, md: 24 }} px={{ base: 6, md: 10 }}>
				<Container maxW="1100px" textAlign="center">
					<Kicker>PRICING</Kicker>
					<Heading
						as="h1"
						mt={5}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '40px', md: '64px' }}
						lineHeight="1.05"
						letterSpacing="-0.025em"
						color={INK}
					>
						All-in pricing.{' '}
						<Box as="span" color={ACCENT}>No hidden fees.</Box>
					</Heading>
					<Text
						mt={6}
						fontSize={{ base: 'lg', md: 'xl' }}
						color={MUTED}
						maxW="720px"
						mx="auto"
						lineHeight="1.55"
					>
						Your first 2 events are free. After that: pay per event, subscribe, or book us to run the floor.
					</Text>
				</Container>
			</Box>

			{/* Per-event bundles */}
			<Box py={{ base: 16, md: 20 }} px={{ base: 6, md: 10 }} bg={CREAM}>
				<Container maxW="1200px">
					<Box textAlign="center" mb={{ base: 10, md: 14 }}>
						<Kicker color={BRAND}>PAY PER EVENT</Kicker>
						<Heading
							as="h2"
							mt={4}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '30px', md: '44px' }}
							color={INK}
						>
							Three tiers, one ladder.
						</Heading>
						<Text mt={3} fontSize="md" color={MUTED} maxW="640px" mx="auto">
							Essential is software-only — for caterers with their own kit and team. Standard and Premium add tablets, an Ops Manager, and runners on-site. No tablet rentals, no hidden labour line.
						</Text>
					</Box>
					<Box
						display="grid"
						gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
						gap={{ base: 6, md: 8 }}
					>
						{PER_EVENT_BUNDLES.map((tier) => (
							<PriceCard key={tier.label} tier={tier} />
						))}
					</Box>
				</Container>
			</Box>

			{/* Subscription */}
			<Box py={{ base: 16, md: 20 }} px={{ base: 6, md: 10 }}>
				<Container maxW="1100px">
					<Box
						bg={BRAND_DEEP}
						color="white"
						borderRadius="24px"
						p={{ base: 8, md: 12 }}
						display="grid"
						gridTemplateColumns={{ base: '1fr', md: '1.2fr 1fr' }}
						gap={{ base: 8, md: 12 }}
						alignItems="center"
					>
						<Box>
							<Kicker color={ACCENT}>SOFTWARE-ONLY · 3+ EVENTS A MONTH</Kicker>
							<Heading
								as="h2"
								mt={4}
								fontFamily={HEAD_FONT}
								fontWeight="700"
								fontSize={{ base: '32px', md: '48px' }}
								color="white"
								lineHeight="1.05"
							>
								Oga.{' '}
								<Box as="span" color={ACCENT}>Self-serve.</Box>
							</Heading>
							<HStack mt={6} spacing={3} align="baseline">
								<Heading fontFamily={HEAD_FONT} fontWeight="800" fontSize={{ base: '44px', md: '64px' }} color="white">
									₦75,000
								</Heading>
								<Text color="rgba(255,255,255,0.7)">/ month · software only</Text>
							</HStack>
							<Text mt={4} color="rgba(255,255,255,0.78)" maxW="420px">
								Unlimited events. No Ops Manager or pre-loaded tablets — you bring your own kit and team.
							</Text>
							<Button
								as={RouterLink}
								to="/register?intent=oga"
								mt={6}
								bg={ACCENT}
								color="white"
								borderRadius="full"
								px={7}
								h="52px"
								fontWeight="700"
								textDecoration="none"
								_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
							>
								Start Oga →
							</Button>
						</Box>
						<VStack align="stretch" spacing={3}>
							<Text fontSize="xs" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(255,255,255,0.55)">
								Included
							</Text>
							{SUBSCRIPTION_INCLUDES.map((feat) => (
								<HStack key={feat} align="flex-start" spacing={3}>
									<Box mt="6px" w="6px" h="6px" borderRadius="full" bg={ACCENT} flexShrink={0} />
									<Text color="rgba(255,255,255,0.92)" lineHeight="1.5">{feat}</Text>
								</HStack>
							))}
							<Text fontSize="xs" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="rgba(255,255,255,0.55)" pt={3}>
								Not included
							</Text>
							{SUBSCRIPTION_EXCLUDES.map((feat) => (
								<HStack key={feat} align="flex-start" spacing={3}>
									<Box mt="3px" w="14px" h="14px" borderRadius="full" border="1.5px solid rgba(255,255,255,0.3)" flexShrink={0} />
									<Text color="rgba(255,255,255,0.62)" lineHeight="1.5" fontSize="sm">{feat}</Text>
								</HStack>
							))}
						</VStack>
					</Box>
				</Container>
			</Box>

			{/* Service add-ons — for Free + Oga customers who need tablets/Ops */}
			<Box py={{ base: 16, md: 22 }} px={{ base: 6, md: 10 }}>
				<Container maxW="1200px">
					<Box textAlign="center" mb={{ base: 10, md: 14 }}>
						<Kicker color={BRAND}>ADD-ONS · FREE + OGA PLANS</Kicker>
						<Heading
							as="h2"
							mt={4}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '28px', md: '40px' }}
							color={INK}
							lineHeight="1.1"
						>
							Need tablets or Ops on a one-off event?
						</Heading>
						<Text mt={4} fontSize="md" color={MUTED} maxW="720px" mx="auto" lineHeight="1.55">
							Free and Oga plans are software-only. If you don't have your own tablets and on-floor team for a specific event, attach a service pack à la carte.
						</Text>
					</Box>

					<Box
						display="grid"
						gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
						gap={{ base: 4, md: 6 }}
						mb={{ base: 10, md: 14 }}
					>
						{SERVICE_ADD_ONS.map((pack) => (
							<Box
								key={pack.label}
								bg="white"
								border={`1px solid ${LINE}`}
								borderRadius="16px"
								p={{ base: 5, md: 6 }}
							>
								<Text fontFamily={HEAD_FONT} fontWeight="700" color={BRAND} fontSize="md">
									{pack.label}
								</Text>
								<Text fontSize="sm" color={MUTED} mb={4}>{pack.range}</Text>
								<Heading fontFamily={HEAD_FONT} fontWeight="800" fontSize="32px" color={INK} lineHeight="1" mb={2}>
									{pack.price}
								</Heading>
								<Text fontSize="xs" color={MUTED} mb={4}>per event</Text>
								<Text fontSize="sm" color={INK} lineHeight="1.5">{pack.kit}</Text>
							</Box>
						))}
					</Box>

					<Box
						bg={CREAM}
						borderRadius="20px"
						p={{ base: 6, md: 10 }}
					>
						<Text
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize="sm"
							color={INK}
							textTransform="uppercase"
							letterSpacing="0.08em"
							mb={6}
						>
							Service-pack policy
						</Text>
						<Box
							display="grid"
							gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
							gap={{ base: 6, md: 8 }}
						>
							{ADD_ON_POLICY.map((item) => (
								<Box key={item.head}>
									<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize="md" color={INK} mb={2}>
										{item.head}
									</Text>
									<Text fontSize="sm" color={MUTED} lineHeight="1.6">
										{item.body}
									</Text>
								</Box>
							))}
						</Box>
					</Box>
				</Container>
			</Box>

			{/* Guest-pay take rate (Layer 3) */}
			<Box py={{ base: 14, md: 18 }} px={{ base: 6, md: 10 }} bg={CREAM}>
				<Container maxW="900px" textAlign="center">
					<Kicker color={BRAND}>WHEN GUESTS PAY FOR EXTRAS</Kicker>
					<Heading
						as="h3"
						mt={4}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '24px', md: '32px' }}
						color={INK}
					>
						2% of premium guest orders, capped at ₦5,000 per transaction.
					</Heading>
					<Text mt={4} fontSize="md" color={MUTED}>
						Cash bar? Premium dishes? Guests pay via Paystack at the table. 98% lands in your bank automatically; we keep 2%. If your event is all-included, nothing here applies.
					</Text>
				</Container>
			</Box>

			{/* Mini-FAQ */}
			<Box py={{ base: 16, md: 20 }} px={{ base: 6, md: 10 }}>
				<Container maxW="900px">
					<Box textAlign="center" mb={{ base: 8, md: 12 }}>
						<Kicker>QUICK QUESTIONS</Kicker>
						<Heading
							as="h2"
							mt={4}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '28px', md: '40px' }}
							color={INK}
						>
							The stuff people ask first.
						</Heading>
					</Box>
					<VStack align="stretch" spacing={6}>
						{FAQ_TEASERS.map((item) => (
							<Box key={item.q} borderTop={`1px solid ${LINE}`} pt={6}>
								<Heading
									as="h3"
									fontFamily={HEAD_FONT}
									fontWeight="600"
									fontSize={{ base: 'lg', md: 'xl' }}
									color={INK}
									mb={3}
								>
									{item.q}
								</Heading>
								<Text fontSize="md" color={MUTED} lineHeight="1.6">{item.a}</Text>
							</Box>
						))}
					</VStack>
					<Box textAlign="center" mt={12}>
						<Text fontSize="md" color={MUTED}>
							Want a longer demo before you commit?{' '}
							<Box as={RouterLink} to="/demo" color={ACCENT_DEEP} fontWeight="700" textDecoration="underline">
								Book a walk-through →
							</Box>
						</Text>
					</Box>
				</Container>
			</Box>

			{/* Final CTA */}
			<Box bg={BRAND} color="white" py={{ base: 16, md: 20 }} px={{ base: 6, md: 10 }}>
				<Container maxW="900px" textAlign="center">
					<Heading
						as="h2"
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '32px', md: '48px' }}
						color="white"
						lineHeight="1.1"
					>
						Your first two events of software are on us.
					</Heading>
					<Text mt={5} color="rgba(255,255,255,0.78)" maxW="600px" mx="auto" fontSize="lg">
						Run a real wedding, see the kitchen flow, then decide whether to keep paying. No card required to start.
					</Text>
					<Button
						as={RouterLink}
						to="/register"
						mt={8}
						bg={ACCENT}
						color="white"
						borderRadius="full"
						px={8}
						h="56px"
						fontSize="lg"
						fontWeight="700"
						textDecoration="none"
						_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
					>
						Create your free account →
					</Button>
				</Container>
			</Box>

			{/* Footer */}
			<Box bg={CREAM} py={10} borderTop={`1px solid ${LINE}`}>
				<Container maxW="1100px">
					<Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
						<Text fontSize="sm" color={MUTED}>© Eventory, a product of Stratospia Limited · Lagos</Text>
						<HStack spacing={6} fontSize="sm">
							<Box as={RouterLink} to="/privacy" color={MUTED} _hover={{ color: INK }}>Privacy</Box>
							<Box as={RouterLink} to="/terms" color={MUTED} _hover={{ color: INK }}>Terms</Box>
							<Box as={RouterLink} to="/demo" color={MUTED} _hover={{ color: INK }}>Demo</Box>
						</HStack>
					</Flex>
				</Container>
			</Box>
		</Box>
	);
};

export default PublicPricingPage;
